import { Suspense, lazy } from "react";
import FullPageLoader from "@/app/admin/_common/FullPageLoader";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes } from "react-router-dom";
import { reportError } from "@/app/_utils/reportError";
import RequireAuth from "./RequireAuth";
import RequirePermission from "./RequirePermission";
import { PERMISSIONS } from "@/app/_utils/permissions";
import AdminShell from "./AdminShell";
import ShowDashLayout from "@/app/admin/shows/[show_id]/layout";
import MerchDashLayout from "@/app/admin/merch/[product_id]/layout";
import CampaignDashLayout from "@/app/admin/marketing/campaigns/[campaignId]/layout";
import AppThemeProvider from "@/app/_utils/providers/AppThemeProvider";
import { SessionAuthProvider } from "@/app/_utils/sessionClient";
import SessionToStoreSync from "@/app/_utils/providers/SessionToStoreSync";
// Shadcn-migrated pages are imported eagerly (not React.lazy) so they mount
// immediately and go straight into their own *Skeleton loading state, instead
// of first flashing the generic PageFallback spinner while the route chunk
// loads - see the Loading states rule in web/cuo-admin/CLAUDE.md.
import AdminFestivals from "@/app/admin/festivals/page";
import AdminFestivalDetail from "@/app/admin/festivals/[festival_id]/page";
import AdminRequests from "@/app/admin/requests/page";
import AdminPerformers from "@/app/admin/performers/page";
import AdminLocations from "@/app/admin/locations/page";
import AdminLocationDetail from "@/app/admin/locations/[location_id]/page";
import AdminShows from "@/app/admin/shows/page";
import ShowGuestList from "@/app/admin/shows/[show_id]/guestlist/page";
import ShowMasterGuestList from "@/app/admin/shows/[show_id]/masterguestlist/page";
import AdminWaitlist from "@/app/admin/waitlist/page";
import AdminGuestDetail from "@/app/admin/guests/[guest_id]/page";
import MarketingLinks from "@/app/admin/marketing/links/page";
import MarketingLinkId from "@/app/admin/marketing/links/[linkId]/page";
import ToolsAutomations from "@/app/admin/tools/automations/page";
import ToolsCommunications from "@/app/admin/tools/communications/page";
import ToolsAutomationDetail from "@/app/admin/tools/automations/[automationId]/page";
import ToolsChecklists from "@/app/admin/tools/checklists/page";
import ToolsChecklistDetail from "@/app/admin/tools/checklists/[templateId]/page";
import AdminPromos from "@/app/admin/promos/page";
import AdminPromoDetail from "@/app/admin/promos/[promoId]/page";
import AdminSettingsUsers from "@/app/admin/settings/users/page";
import AdminSettingsRoles from "@/app/admin/settings/roles/page";
import AdminNoAccess from "@/app/admin/no-access/page";
const LoginPage = lazy(() => import("@/app/(public)/login/page"));

const AdminIndex = lazy(() => import("@/app/admin/page"));
const AdminHome = lazy(() => import("@/app/admin/home/page"));
const AdminOrders = lazy(() => import("@/app/admin/orders/page"));
const AdminPasswords = lazy(() => import("@/app/admin/passwords/page"));
const AdminAnalytics = lazy(() => import("@/app/admin/analytics/page"));
const SettingsLayout = lazy(() => import("@/app/admin/settings/layout"));
const AdminSettingsEvents = lazy(() => import("@/app/admin/settings/events/page"));
const AdminNewShow = lazy(() => import("@/app/admin/shows/newshow/page"));
const ShowRelatedShows = lazy(() => import("@/app/admin/shows/[show_id]/relatedshows/page"));
const ShowIntakeList = lazy(() => import("@/app/admin/shows/[show_id]/intakelist/page"));
const ShowLineup = lazy(() => import("@/app/admin/shows/[show_id]/lineup/page"));
const ShowExpenses = lazy(() => import("@/app/admin/shows/[show_id]/expenses/page"));
const ShowChecklist = lazy(() => import("@/app/admin/shows/[show_id]/checklist/page"));
const ShowDetails = lazy(() => import("@/app/admin/shows/[show_id]/details/page"));
const ShowSettings = lazy(() => import("@/app/admin/shows/[show_id]/settings/page"));
const ShowCheckouts = lazy(() => import("@/app/admin/shows/[show_id]/checkouts/page"));
const ShowLiveSales = lazy(() => import("@/app/admin/shows/[show_id]/live/page"));
const AdminMerch = lazy(() => import("@/app/admin/merch/page"));
const AdminAddMerch = lazy(() => import("@/app/admin/merch/add-merch/page"));
const MerchSettings = lazy(() => import("@/app/admin/merch/[product_id]/settings/page"));
const MerchOrders = lazy(() => import("@/app/admin/merch/[product_id]/orders/page"));
const MerchDetails = lazy(() => import("@/app/admin/merch/[product_id]/details/page"));
const MarketingHome = lazy(() => import("@/app/admin/marketing/page"));
const MarketingCampaigns = lazy(() => import("@/app/admin/marketing/campaigns/page"));
const MarketingEmails = lazy(() => import("@/app/admin/marketing/emails/page"));
const MarketingTemplates = lazy(() => import("@/app/admin/marketing/templates/page"));
const MarketingNewTemplate = lazy(
  () => import("@/app/admin/marketing/templates/newtemplate/page")
);
const MarketingTemplateId = lazy(
  () => import("@/app/admin/marketing/templates/[templateId]/page")
);
const MarketingEmailId = lazy(() => import("@/app/admin/marketing/emails/[emailId]/page"));
const CampaignSchedule = lazy(
  () => import("@/app/admin/marketing/campaigns/[campaignId]/schedule/page")
);
const CampaignPerformance = lazy(
  () => import("@/app/admin/marketing/campaigns/[campaignId]/performance/page")
);
const CampaignOverview = lazy(
  () => import("@/app/admin/marketing/campaigns/[campaignId]/overview/page")
);
const CampaignContent = lazy(
  () => import("@/app/admin/marketing/campaigns/[campaignId]/content/page")
);

function PageFallback() {
  return <FullPageLoader />;
}

function RootErrorFallback() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 16,
        padding: 24,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" fontWeight={600}>
        Something went wrong
      </Typography>
      <Typography variant="body2" color="text.secondary">
        An unexpected error occurred. Please try refreshing the page.
      </Typography>
      <Button variant="outlined" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary
      FallbackComponent={RootErrorFallback}
      onError={(error) => reportError(error, "root")}
    >
    <SessionAuthProvider>
      <SessionToStoreSync />
      <AppThemeProvider>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/admin" replace />} />
              <Route path="/login" element={<LoginPage />} />

              <Route element={<RequireAuth />}>
                <Route path="/admin" element={<AdminShell />}>
                  <Route index element={<AdminIndex />} />
                  <Route path="no-access" element={<AdminNoAccess />} />
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_HOME} />}>
                    <Route path="home" element={<AdminHome />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_ORDERS} />}>
                    <Route path="orders" element={<AdminOrders />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_PROMOS} />}>
                    <Route path="promos" element={<AdminPromos />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_PROMOS} />}>
                    <Route path="promos/:promoId" element={<AdminPromoDetail />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_PASSWORDS} />}>
                    <Route path="passwords" element={<AdminPasswords />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_REQUESTS} />}>
                    <Route path="requests" element={<AdminRequests />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_WAITLIST} />}>
                    <Route path="waitlist" element={<AdminWaitlist />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_GUESTS} />}>
                    <Route path="guests/:guest_id" element={<AdminGuestDetail />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_PERFORMERS} />}>
                    <Route path="performers" element={<AdminPerformers />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_LOCATIONS} />}>
                    <Route path="locations" element={<AdminLocations />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_LOCATIONS} />}>
                    <Route path="locations/:location_id" element={<AdminLocationDetail />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_FESTIVALS} />}>
                    <Route path="festivals" element={<AdminFestivals />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_FESTIVALS} />}>
                    <Route path="festivals/:festival_id" element={<AdminFestivalDetail />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_ANALYTICS} />}>
                    <Route path="analytics" element={<AdminAnalytics />} />
                  </Route>
                  <Route path="settings" element={<SettingsLayout />}>
                    <Route index element={<Navigate to="events" replace />} />
                    <Route path="events" element={<AdminSettingsEvents />} />
                    <Route element={<RequirePermission permission={PERMISSIONS.PAGE_SETTINGS_USERS} />}>
                      <Route path="users" element={<AdminSettingsUsers />} />
                    </Route>
                    <Route element={<RequirePermission permission={PERMISSIONS.PAGE_SETTINGS_ROLES} />}>
                      <Route path="roles" element={<AdminSettingsRoles />} />
                    </Route>
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_SHOWS} />}>
                    <Route path="shows" element={<AdminShows />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.SHOW_WRITE} />}>
                    <Route path="shows/newshow" element={<AdminNewShow />} />
                  </Route>
                  <Route path="shows/:show_id" element={<ShowDashLayout />}>
                    <Route
                      index
                      element={<Navigate to="guestlist" replace />}
                    />
                    <Route path="relatedshows" element={<ShowRelatedShows />} />
                    <Route path="masterguestlist" element={<ShowMasterGuestList />} />
                    <Route path="intakelist" element={<ShowIntakeList />} />
                    <Route path="guestlist" element={<ShowGuestList />} />
                    <Route path="details" element={<ShowDetails />} />
                    <Route path="live" element={<ShowLiveSales />} />
                    <Route path="lineup" element={<ShowLineup />} />
                    <Route path="expenses" element={<ShowExpenses />} />
                    <Route path="checkouts" element={<ShowCheckouts />} />
                    <Route path="checklist" element={<ShowChecklist />} />
                    <Route path="settings" element={<ShowSettings />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_MERCH} />}>
                    <Route path="merch" element={<AdminMerch />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.MERCH_WRITE} />}>
                    <Route path="merch/add-merch" element={<AdminAddMerch />} />
                  </Route>
                  <Route path="merch/:product_id" element={<MerchDashLayout />}>
                    <Route
                      index
                      element={<Navigate to="details" replace />}
                    />
                    <Route path="settings" element={<MerchSettings />} />
                    <Route path="orders" element={<MerchOrders />} />
                    <Route path="details" element={<MerchDetails />} />
                  </Route>
                  <Route path="marketing" element={<Navigate to="marketing/campaigns" replace />} />
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_CAMPAIGNS} />}>
                    <Route path="marketing/campaigns" element={<MarketingCampaigns />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_EMAILS} />}>
                    <Route path="marketing/emails" element={<MarketingEmails />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_TEMPLATES} />}>
                    <Route path="marketing/templates" element={<MarketingTemplates />} />
                  </Route>
                  <Route
                    path="marketing/templates/newtemplate"
                    element={<MarketingNewTemplate />}
                  />
                  <Route
                    path="marketing/templates/:templateId"
                    element={<MarketingTemplateId />}
                  />
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_LINKS} />}>
                    <Route path="marketing/links" element={<MarketingLinks />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_LINKS} />}>
                    <Route path="marketing/links/:linkId" element={<MarketingLinkId />} />
                  </Route>
                  <Route path="tools" element={<Navigate to="/admin/tools/automations" replace />} />
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_AUTOMATIONS} />}>
                    <Route path="tools/automations" element={<ToolsAutomations />} />
                  </Route>
            <Route element={<RequirePermission permission={PERMISSIONS.PAGE_COMMUNICATIONS} />}>
                    <Route path="tools/communications" element={<ToolsCommunications />} />
                  </Route>
                  <Route element={<RequirePermission permission={PERMISSIONS.PAGE_CHECKLISTS} />}>
                    <Route path="tools/checklists" element={<ToolsChecklists />} />
                    <Route path="tools/checklists/new" element={<ToolsChecklistDetail />} />
                    <Route path="tools/checklists/:templateId" element={<ToolsChecklistDetail />} />
                  </Route>
                  <Route
                    path="tools/automations/:automationId"
                    element={<ToolsAutomationDetail />}
                  />
                  <Route path="marketing/emails/:emailId" element={<MarketingEmailId />} />
                  <Route path="marketing/campaigns/:campaignId" element={<CampaignDashLayout />}>
                    <Route
                      index
                      element={<Navigate to="overview" replace />}
                    />
                    <Route path="schedule" element={<CampaignSchedule />} />
                    <Route path="performance" element={<CampaignPerformance />} />
                    <Route path="overview" element={<CampaignOverview />} />
                    <Route path="content" element={<CampaignContent />} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </Suspense>
      </AppThemeProvider>
    </SessionAuthProvider>
    </ErrorBoundary>
  );
}
